<!-- SalesmenChatbot.svelte -->
<svelte:options customElement="salesmen-chatbot" />

<script>
  import { onDestroy, onMount, tick } from 'svelte';

  // Props (custom-element attributes: chat-endpoint, session-id, csrf-token, fab, recaptcha-site-key)
  export let chatEndpoint = '/admin/chat';
  export let sessionId = '';
  // FAB mode: the host pages render this widget as a fixed bottom-right launcher that starts
  // collapsed and fetches nothing until a visitor opens it. Non-fab (admin) pages are unchanged.
  export let fab = false;
  export let recaptchaSiteKey = '';

  // Angle of the FAB's rainbow border ring, in degrees. Driven from requestAnimationFrame rather
  // than a CSS @property keyframe animation: animating a custom property inside a shadow-DOM
  // stylesheet is unreliable across browsers (the border would periodically fail to repaint), so
  // the loop writes the angle as a plain inline style each frame instead.
  let fabAngle = 0;
  let fabAngleFrame;

  // quarkus-rest-csrf rejects any unsafe-method request whose Content-Type isn't form-urlencoded
  // or multipart *unless* it carries the token in this header instead - the header path is what
  // lets the POSTs below stay JSON. The token itself has to come from the host page: the CSRF
  // cookie is httpOnly by default, so this component cannot read it itself. Render it server-side
  // with Qute's `{inject:csrf.token}` (the same source the app's HTML forms use) into the
  // `csrf-token` attribute, e.g. `<salesmen-chatbot csrf-token="{inject:csrf.token}" ...>`.
  export let csrfToken = '';
  const csrfHeaderName = 'X-CSRF-TOKEN';

  // Greeting bubble shown ahead of the transcript on every mount. Configured server-side (see
  // AdminChatController#getPreamble), not agent-generated: it is never sent as a turn and never
  // saved on the session, so it has to be fetched and rendered independently of loadHistory below.
  let preamble = '';

  // Header/appearance state. Both are purely presentational and local to this mount: neither is
  // persisted, so a page reload always comes back expanded with the disclaimer showing again.
  let collapsed = false;
  let disclaimerDismissed = false;
  // True once the preamble (and history, when a session id was given) has been requested. In FAB
  // mode that happens on first open, not on mount - the bundle loads on every page, the network
  // work must not.
  let initialized = false;

  // Transcript: { role: 'USER'|'ASSISTANT'|'SYSTEM'|'TOOL', text, sources, createdAt, pending, error }
  let turns = [];
  let composerText = '';
  let sending = false;
  let loadingHistory = false;
  let loadError = '';
  let currentSessionId = '';
  let sessionTitle = '';
  let scrollEl;
  let host = null;
  let composerEl;

  $: currentSessionId = currentSessionId || sessionId || '';

  onMount(() => {
    host = composerEl?.getRootNode()?.host;

    // Custom-element attribute -> prop sync can lag the initial render; read the DOM
    // attribute directly as a fallback, same pattern used by phone-input.
    if (host && !sessionId) {
      const attr = host.getAttribute('session-id');
      if (attr) sessionId = attr;
    }
    if (host && chatEndpoint === '/admin/chat') {
      const attr = host.getAttribute('chat-endpoint');
      if (attr) chatEndpoint = attr;
    }
    if (host && !csrfToken) {
      const attr = host.getAttribute('csrf-token');
      if (attr) csrfToken = attr;
    }
    if (host) {
      fab = fab || host.hasAttribute('fab') || host.hasAttribute('start-collapsed');
      if (!recaptchaSiteKey) {
        const attr = host.getAttribute('recaptcha-site-key');
        if (attr) recaptchaSiteKey = attr;
      }
    }

    currentSessionId = sessionId || '';

    if (fab) {
      // FAB mode: sit collapsed, fetch nothing, until a visitor opens the panel.
      collapsed = true;
      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        startFabBorderSpin();
      }
      return;
    }

    initChat();
  });

  /**
   * Rotates the border ring one full turn every six seconds. rAF pauses itself in background
   * tabs, so a hidden page costs nothing.
   */
  function startFabBorderSpin() {
    const tick = (timestamp) => {
      fabAngle = (timestamp / 1000) * 60 % 360;
      fabAngleFrame = requestAnimationFrame(tick);
    };
    fabAngleFrame = requestAnimationFrame(tick);
  }

  onDestroy(() => {
    if (fabAngleFrame) {
      cancelAnimationFrame(fabAngleFrame);
    }
  });

  /**
   * First-open network work: the preamble (and history, when a session id was mounted in) is
   * requested exactly once per mount. Non-fab mounts call this from onMount; fab mounts from the
   * launcher click, so a page view costs no requests until the visitor actually wants to chat.
   */
  function initChat() {
    if (initialized) return;
    initialized = true;
    loadPreamble();
    if (currentSessionId) {
      loadHistory(currentSessionId);
    }
  }

  function openChat() {
    collapsed = false;
    initChat();
  }

  async function loadPreamble() {
    try {
      const res = await fetch(`${chatEndpoint}/preamble`, {
        method: 'GET',
        headers: { Accept: 'application/json' }
      });
      if (!res.ok) return;
      const data = await res.json();
      preamble = data.preamble || '';
      // The public preamble also carries the reCAPTCHA site key the first message needs; the
      // attribute remains a fallback for pages that can render the key server-side.
      if (data.siteKey) recaptchaSiteKey = data.siteKey;
    } catch (e) {
      // The greeting is a nicety, not a dependency: a failed fetch just leaves it blank rather
      // than blocking the rest of the component.
    }
  }

  async function loadHistory(id) {
    loadingHistory = true;
    loadError = '';
    try {
      const res = await fetch(`${chatEndpoint}/${id}`, {
        method: 'GET',
        headers: { Accept: 'application/json' }
      });
      if (!res.ok) {
        const body = await safeJson(res);
        throw new Error(body?.error || `Could not load this conversation (HTTP ${res.status}).`);
      }
      const session = await res.json();
      sessionTitle = session.title || '';
      turns = (session.messages || [])
        .filter((m) => m.role === 'USER' || m.role === 'ASSISTANT')
        .map((m) => ({
          role: m.role,
          text: textOf(m),
          images: imagesOf(m),
          sources: m.sources || [],
          createdAt: m.createdAt
        }));
      await scrollToBottom();
    } catch (e) {
      loadError = e.message || 'Could not load this conversation.';
    } finally {
      loadingHistory = false;
    }
  }

  function textOf(message) {
    return (message.parts || [])
      .filter((p) => p.partType === 'TEXT')
      .map((p) => p.text)
      .join('\n\n');
  }

  function imagesOf(message) {
    return (message.parts || [])
      .filter((p) => p.partType !== 'TEXT')
      .map((p) => imagePartSummary(p));
  }

  function imagePartSummary(part) {
    let summary = 'Image';
    if (part.mediaType) summary += ` (${part.mediaType})`;
    if (part.width && part.height) summary += ` ${part.width}×${part.height}`;
    if (part.sizeBytes != null) summary += ` · ${formatBytes(part.sizeBytes)}`;
    return summary;
  }

  function formatBytes(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  function sourceLabel(source) {
    let label = source.documentTitle || source.sourceUri || 'Untitled document';
    return source.documentVersion ? `${label} (${source.documentVersion})` : label;
  }

  function isLinkableSource(source) {
    if (!source?.sourceUri) return false;
    const uri = source.sourceUri.trim().toLowerCase();
    return uri.startsWith('http://') || uri.startsWith('https://');
  }

  /**
   * Loads Google's reCAPTCHA v3 script once per page. The script is only ever requested here - on
   * a visitor's first message, never on page load - so the widget's presence on every page costs
   * no third-party requests until someone actually chats.
   *
   * Known corner: pages that already loaded the v2 api.js (the /help/contact form) register
   * window.grecaptcha from the v2 loader, so this loader skips and execute() fails visibly rather
   * than silently - the visitor gets the verification-failed bubble instead of a broken widget.
   */
  /**
   * v3 renders Google's floating badge in the page's bottom-right corner - exactly where the FAB
   * sits. Google allows hiding the badge as long as the reCAPTCHA branding appears in the user
   * flow (the disclaimer carries it), so the badge is hidden once the script has loaded. The
   * badge lives in the main document, outside this component's shadow root, hence the JS rather
   * than a scoped style.
   */
  function hideRecaptchaBadge() {
    const badge = document.querySelector('.grecaptcha-badge');
    if (badge) badge.style.visibility = 'hidden';
  }

  function loadRecaptchaScript() {
    return new Promise((resolve) => {
      if (window.grecaptcha) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(recaptchaSiteKey)}`;
      script.async = true;
      script.onload = () => {
        hideRecaptchaBadge();
        resolve();
      };
      script.onerror = (e) => {
        // Surface the real cause (wrong key type, domain restriction, blocked request) instead
        // of leaving only the generic user-facing error.
        console.error('[salesmen-chatbot] reCAPTCHA api.js failed to load', e);
        resolve();
      };
      document.head.appendChild(script);
    });
  }

  async function getCaptchaToken() {
    if (!recaptchaSiteKey) {
      throw new Error('This chat is not available right now. Please try again later.');
    }
    await loadRecaptchaScript();
    if (!window.grecaptcha) {
      throw new Error('Could not load reCAPTCHA. Please check your connection and try again.');
    }
    return new Promise((resolve, reject) => {
      window.grecaptcha.ready(() => {
        window.grecaptcha
          .execute(recaptchaSiteKey, { action: 'chat_new' })
          .then((token) => {
            // The badge can appear only once the first token has been issued, so hide it again
            // here for good measure.
            hideRecaptchaBadge();
            resolve(token);
          })
          .catch(() => reject(new Error('reCAPTCHA verification failed. Please try again.')));
      });
    });
  }

  async function safeJson(res) {
    try {
      return await res.json();
    } catch (e) {
      return null;
    }
  }

  async function scrollToBottom() {
    await tick();
    if (scrollEl) scrollEl.scrollTop = scrollEl.scrollHeight;
  }

  function handleComposerKeydown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  async function ensureSession() {
    if (currentSessionId) return currentSessionId;

    // Public sessions are gated by reCAPTCHA v3: the invisible token goes with the create call
    // and is verified server-side before any model work happens. Admin mounts never send it.
    let body = {};
    if (fab) {
      body = { captchaToken: await getCaptchaToken() };
    }

    const res = await fetch(`${chatEndpoint}/new`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        [csrfHeaderName]: csrfToken
      },
      body: JSON.stringify(body)
    });
    if (!res.ok) {
      const body = await safeJson(res);
      throw new Error(body?.error || `Could not start a conversation (HTTP ${res.status}).`);
    }
    const created = await res.json();
    currentSessionId = created.id;
    sessionTitle = created.title || '';

    host?.dispatchEvent(new CustomEvent('session-created', {
      detail: { sessionId: currentSessionId },
      bubbles: true,
      composed: true
    }));

    return currentSessionId;
  }

  /**
   * Resets the widget to a blank conversation. No backend call is needed: clearing the session id
   * just lets ensureSession() lazily open a fresh one on the next message, exactly as it does for
   * a first-ever visit.
   */
  function startNewChat() {
    turns = [];
    composerText = '';
    loadError = '';
    sessionId = '';
    currentSessionId = '';
    sessionTitle = '';

    host?.dispatchEvent(new CustomEvent('chat-reset', { bubbles: true, composed: true }));
  }

  async function sendMessage() {
    const message = composerText.trim();
    if (!message || sending) return;

    sending = true;
    composerText = '';

    const userTurn = { role: 'USER', text: message, images: [], sources: [] };
    turns = [...turns, userTurn];
    await scrollToBottom();

    try {
      const id = await ensureSession();

      const res = await fetch(`${chatEndpoint}/${id}/converse`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          [csrfHeaderName]: csrfToken
        },
        body: JSON.stringify({ message })
      });

      if (!res.ok) {
        const body = await safeJson(res);
        throw new Error(body?.error || `The agent did not answer (HTTP ${res.status}).`);
      }

      const reply = await res.json();
      if (reply.sessionTitle) sessionTitle = reply.sessionTitle;

      turns = [...turns, {
        role: 'ASSISTANT',
        text: reply.reply || '',
        images: [],
        sources: reply.sources || [],
        createdAt: reply.repliedAt
      }];

      host?.dispatchEvent(new CustomEvent('turn-completed', {
        detail: { sessionId: id, reply },
        bubbles: true,
        composed: true
      }));
    } catch (e) {
      // Give the admin their text back so a failed turn is not silently lost.
      composerText = message;
      turns = [...turns, { role: 'ASSISTANT', error: e.message || 'Something went wrong.' }];
    } finally {
      sending = false;
      await scrollToBottom();
    }
  }
</script>

{#if collapsed}
<button type="button" class="reopen-btn" style:--fab-angle="{fabAngle}deg" on:click={openChat} aria-label="Open chat">
  <img src="/mrhk_ring_logo.svg" alt="" class="reopen-logo" />
  <span>Chat</span>
</button>
{:else}
<div class="chatbot">
  <div class="header">
    <img src="/favicon.svg" alt="MRHK" class="header-logo" />
    <span class="header-title">MRHK Assistant</span>
    <div class="header-actions">
      <button type="button" class="icon-btn" title="New chat" aria-label="Start a new chat" on:click={startNewChat}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <polyline points="23 4 23 10 17 10"></polyline>
          <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
        </svg>
      </button>
      <button type="button" class="icon-btn" title="Minimize" aria-label="Minimize chat" on:click={() => (collapsed = true)}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <line x1="5" y1="12" x2="19" y2="12"></line>
        </svg>
      </button>
    </div>
  </div>

  <div class="transcript" bind:this={scrollEl}>
    {#if preamble}
      <div class="turn turn-assistant">
        <div class="bubble">
          <p class="bubble-text">{preamble}</p>
        </div>
      </div>
    {/if}

    {#if loadingHistory}
      <p class="status-line">Loading conversation…</p>
    {:else if loadError}
      <p class="status-line error">{loadError}</p>
    {:else if turns.length === 0 && !preamble}
      <p class="status-line">No messages yet. Say hello to start the conversation.</p>
    {/if}

    {#each turns as turn}
      <div class="turn" class:turn-user={turn.role === 'USER'} class:turn-assistant={turn.role !== 'USER'}>
        <div class="bubble" class:bubble-error={!!turn.error}>
          {#if turn.error}
            <p class="bubble-text">{turn.error}</p>
          {:else}
            <p class="bubble-text">{turn.text}</p>
            {#each turn.images || [] as image}
              <p class="image-chip">{image}</p>
            {/each}
            {#if turn.sources && turn.sources.length > 0}
              <div class="sources">
                <span class="sources-label">Sources:</span>
                {#each turn.sources as source, idx}
                  {#if idx > 0}<span class="sources-sep">·</span>{/if}
                  {#if isLinkableSource(source)}
                    <a href={source.sourceUri} target="_blank" rel="noopener noreferrer">{sourceLabel(source)}</a>
                  {:else}
                    <span>{sourceLabel(source)}</span>
                  {/if}
                {/each}
              </div>
            {/if}
          {/if}
        </div>
      </div>
    {/each}

    {#if sending}
      <div class="turn turn-assistant">
        <div class="bubble bubble-pending">
          <span class="typing-dot"></span><span class="typing-dot"></span><span class="typing-dot"></span>
        </div>
      </div>
    {/if}
  </div>

  <div class="composer">
    <textarea
      bind:this={composerEl}
      bind:value={composerText}
      on:keydown={handleComposerKeydown}
      placeholder="Type a message…"
      disabled={sending}
      rows="2"
    ></textarea>
    <button type="button" on:click={sendMessage} disabled={sending || !composerText.trim()}>
      {sending ? 'Sending…' : 'Send'}
    </button>
  </div>

  {#if !disclaimerDismissed}
  <div class="disclaimer">
    <p>
      This assistant's replies are generated with the help of AI and may contain mistakes. By
      continuing to chat, you agree to this conversation being recorded and used to improve our
      support, in line with our
      <a href="/legal/privacy-policy.html" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
      and
      <a href="/preferences/cookies" target="_blank" rel="noopener noreferrer">Cookie Preferences</a>.
    </p>
    {#if fab}
    <!-- Required by Google's terms when the reCAPTCHA badge is hidden: the branding must appear
         in the user flow instead. -->
    <p class="recaptcha-note">
      This site is protected by reCAPTCHA and the Google
      <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
      and
      <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
      apply.
    </p>
    {/if}
    <button type="button" class="disclaimer-dismiss" aria-label="Dismiss disclaimer" on:click={() => (disclaimerDismissed = true)}>×</button>
  </div>
  {/if}
</div>
{/if}

<style>
  :host {
    display: block;
    height: 100%;
  }

  .chatbot {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 24rem;
    background: var(--surface-lowest);
    border-radius: 0.75rem;
    overflow: hidden;
    font-family: var(--font-sans, system-ui, sans-serif);
  }

  .header {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    padding: 0.75rem 1rem;
    background: var(--primary);
    color: var(--on-primary, #fff);
    flex-shrink: 0;
  }

  .header-logo {
    width: 1.75rem;
    height: 1.75rem;
    border-radius: 50%;
    background: #fff;
    padding: 0.15rem;
    box-sizing: border-box;
    flex-shrink: 0;
  }

  .header-title {
    flex: 1;
    font-size: 0.95rem;
    font-weight: 600;
  }

  .header-actions {
    display: flex;
    gap: 0.25rem;
  }

  .icon-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.75rem;
    height: 1.75rem;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.15);
    color: inherit;
    cursor: pointer;
    transition: background-color 0.15s ease;
  }

  .icon-btn:hover {
    background: rgba(255, 255, 255, 0.28);
  }

  .reopen-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem 0.5rem 0.5rem;
    border: 0;
    border-radius: 999px;
    background: var(--primary);
    color: var(--on-primary, #fff);
    font-family: var(--font-sans, system-ui, sans-serif);
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    box-shadow: 0 4px 12px -4px rgba(0, 0, 0, 0.3);
  }

  .reopen-logo {
    width: 1.5rem;
    height: 1.5rem;
    border-radius: 50%;
    background: #fff;
    padding: 0.15rem;
    box-sizing: border-box;
  }

  .disclaimer {
    position: relative;
    flex-shrink: 0;
    padding: 0.625rem 1.75rem 0.625rem 0.875rem;
    background: var(--surface-container);
    border-top: 1px solid var(--surface-high, rgba(0, 0, 0, 0.08));
  }

  .disclaimer p {
    margin: 0;
    font-size: 0.7rem;
    font-style: italic;
    line-height: 1.4;
    color: var(--on-surface-variant);
  }

  .disclaimer p + p {
    margin-top: 0.375rem;
  }

  .recaptcha-note {
    font-style: normal;
  }

  .disclaimer a {
    color: inherit;
    text-decoration: underline;
  }

  .disclaimer-dismiss {
    position: absolute;
    top: 0.375rem;
    right: 0.5rem;
    width: 1.25rem;
    height: 1.25rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 0;
    background: transparent;
    color: var(--on-surface-variant);
    font-size: 1rem;
    line-height: 1;
    cursor: pointer;
    border-radius: 50%;
  }

  .disclaimer-dismiss:hover {
    background: var(--surface-high, rgba(0, 0, 0, 0.08));
  }

  .transcript {
    flex: 1;
    overflow-y: auto;
    padding: 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .status-line {
    margin: 0;
    padding: 0.5rem 0;
    color: var(--on-surface-variant);
    font-size: 0.875rem;
    text-align: center;
  }

  .status-line.error {
    color: var(--color-error, #d32f2f);
  }

  .turn {
    display: flex;
  }

  .turn-user {
    justify-content: flex-end;
  }

  .turn-assistant {
    justify-content: flex-start;
  }

  .bubble {
    max-width: 78%;
    padding: 0.625rem 0.875rem;
    border-radius: 0.875rem;
    font-size: 0.9rem;
    line-height: 1.45;
    color: var(--on-surface);
    background: var(--surface-container);
  }

  .turn-user .bubble {
    background: var(--primary);
    color: var(--on-primary, #fff);
    border-bottom-right-radius: 0.25rem;
  }

  .turn-assistant .bubble {
    border-bottom-left-radius: 0.25rem;
  }

  .bubble-error {
    background: color-mix(in srgb, var(--color-error, #d32f2f) 12%, var(--surface-container));
    color: var(--color-error, #d32f2f);
  }

  .bubble-text {
    margin: 0;
    white-space: pre-wrap;
    word-break: break-word;
  }

  .image-chip {
    margin: 0.375rem 0 0;
    padding: 0.25rem 0.5rem;
    display: inline-block;
    border-radius: 0.375rem;
    background: var(--surface-high, rgba(0, 0, 0, 0.06));
    font-size: 0.75rem;
    color: var(--on-surface-variant);
  }

  .sources {
    margin-top: 0.5rem;
    padding-top: 0.375rem;
    border-top: 1px solid var(--surface-high, rgba(0, 0, 0, 0.08));
    font-size: 0.75rem;
    color: var(--on-surface-variant);
  }

  .sources a {
    color: inherit;
    text-decoration: underline;
  }

  .sources-label {
    font-weight: 600;
    margin-right: 0.25rem;
  }

  .sources-sep {
    margin: 0 0.375rem;
  }

  .bubble-pending {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.75rem 1rem;
  }

  .typing-dot {
    width: 0.4rem;
    height: 0.4rem;
    border-radius: 50%;
    background: var(--on-surface-variant);
    opacity: 0.5;
    animation: typing-blink 1.2s infinite ease-in-out;
  }

  .typing-dot:nth-child(2) {
    animation-delay: 0.2s;
  }

  .typing-dot:nth-child(3) {
    animation-delay: 0.4s;
  }

  @keyframes typing-blink {
    0%, 80%, 100% { opacity: 0.3; }
    40% { opacity: 1; }
  }

  .composer {
    display: flex;
    gap: 0.5rem;
    align-items: flex-end;
    padding: 0.75rem;
    border-top: 1px solid var(--surface-high, rgba(0, 0, 0, 0.08));
    background: var(--surface-lowest);
  }

  .composer textarea {
    flex: 1;
    resize: none;
    padding: 0.625rem 0.75rem;
    border: 0;
    border-radius: 0.5rem;
    background: var(--surface-container);
    color: var(--on-surface);
    font-family: inherit;
    font-size: 0.9rem;
    outline: none;
    box-sizing: border-box;
  }

  .composer textarea:focus-visible {
    box-shadow: inset 0 -2px 0 0 var(--primary);
  }

  .composer textarea:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .composer button {
    flex-shrink: 0;
    padding: 0.625rem 1.125rem;
    border: 0;
    border-radius: 0.5rem;
    background: var(--primary);
    color: var(--on-primary, #fff);
    font-family: inherit;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: opacity 0.15s ease;
  }

  .composer button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* ------------------------------------------------------------
   * FAB mode (:host([fab])): the host takes no layout space and the
   * launcher/panel float bottom-right, fixed to the viewport. Non-fab
   * (admin) mounts keep the in-page card layout untouched above.
   *
   * The launcher is the ring mark itself on a white circular backing: no
   * pill, the button is a circle sized to the mark and the brand's own warm
   * segmented ring hugs it. The border itself is the motion: a sharp 3px
   * conic ring in deepened ring hues, slowly rotating around the circle,
   * over a tight static glow in the true ring colors. The rotation is the
   * single bit of movement here - everything else stays quiet.
   * ------------------------------------------------------------ */
  :host([fab]) {
    display: block;
    height: 0;
  }

  :host([fab]) .reopen-btn {
    position: fixed;
    right: 1rem;
    bottom: 1rem;
    z-index: 1000;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.65rem;
    border: 3px solid transparent;
    border-radius: 50%;
    /* sharp brand ring at the circle's edge (padding-box/border-box trick), in
     * the logo's segment order - cream, warm gray, cream, tan, terracotta,
     * brown, terracotta, tan - saturated so each hue reads distinctly on
     * white. The
     * angle is updated inline from a requestAnimationFrame loop (see
     * startFabBorderSpin) - CSS keyframe animation of custom properties is
     * unreliable inside shadow DOM and caused the ring to periodically vanish. */
    background:
      linear-gradient(#ffffff, #ffffff) padding-box,
      conic-gradient(from var(--fab-angle), #e3c084, #cbc0a6, #e3c084, #a8845d, #c65f2e, #6b3315, #c65f2e, #a8845d, #e3c084) border-box;
    box-shadow: 0 10px 28px -12px rgba(45, 28, 15, 0.45);
    transition: transform 0.2s ease;
  }

  /* Tight static glow: the true ring colors, blurred just enough to read as
   * light, hugging the circular backing. Deliberately not animated - the
   * border is the motion, the glow is only the light. */
  :host([fab]) .reopen-btn::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 5.5rem;
    height: 5.5rem;
    margin: -2.75rem 0 0 -2.75rem;
    border-radius: 50%;
    background: conic-gradient(from 0deg, #f2e0c5, #ebe8e3, #f2e0c5, #d6c3b6, #d5835a, #904c27, #d5835a, #d6c3b6, #f2e0c5);
    filter: blur(10px);
    opacity: 0.55;
    z-index: -1;
  }

  :host([fab]) .reopen-btn:hover {
    transform: scale(1.06);
  }

  :host([fab]) .reopen-btn:focus-visible {
    outline: 3px solid rgba(213, 131, 90, 0.7);
    outline-offset: 3px;
  }

  :host([fab]) .reopen-logo {
    width: 3rem;
    height: 3rem;
    background: transparent;
    padding: 0;
    border-radius: 0;
  }

  /* FAB is mark-only: the label stays hidden on every viewport. */
  :host([fab]) .reopen-btn span {
    display: none;
  }

  /* The rAF border spin never starts under reduced motion (see onMount); this
   * only keeps the hover scale off for those visitors too. */
  @media (prefers-reduced-motion: reduce) {
    :host([fab]) .reopen-btn:hover {
      transform: none;
    }
  }

  :host([fab]) .chatbot {
    position: fixed;
    right: 1rem;
    bottom: 1rem;
    z-index: 1000;
    width: min(24rem, calc(100vw - 2rem));
    height: min(38rem, calc(100vh - 2rem));
    min-height: 0;
    box-shadow: 0 12px 40px -8px rgba(0, 0, 0, 0.35);
  }
</style>
